import cn from 'classnames';
import PropTypes from 'prop-types';
import queryString from 'query-string';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Link } from 'react-router';

import {
  CART_STEP,
  CHECKOUT_PAGE_MAP,
  EDIT_ADDRESS_STEP,
  EDIT_BILLING_ADDRESS_STEP,
  NEW_ADDRESS_STEP,
  NEW_BILLING_ADDRESS_STEP,
  NEW_PAYMENT_STEP,
  REVIEW_STEP,
  SAVED_ADDRESS_STEP,
  SELECT_BILLING_ADDRESS_STEP,
  TBD_STEP
} from 'constants/checkoutFlow';
import {
  HYDRA_CHECKOUT_MOVE_TO_FAVORITES,
  HYDRA_CHECKOUT_SHIP_SECTION_ADJUSTMENT
} from 'constants/hydraTests';
import { pvCheckout } from 'events/checkout';

import { trackEvent } from 'helpers/analytics';
import { isAssigned, triggerAssignment } from 'actions/ab';
import { resetCartState } from 'actions/cart';
import {
  addToCheckoutReferrerHistory,
  fetchCheckoutData,
  goToCart,
  handleInvalidCheckoutPath,
  removeFromCheckoutReferrerHistory,
  resetToDefaultState,
  trackHasTrackedPageView,
  updateCheckoutForTargetStep
} from 'actions/checkout/purchase';
import { titaniteView, track } from 'apis/amethyst';
import CheckoutGiftOptionsSection from 'containers/Checkout/GiftOptionsSection';
import CheckoutPaySection from 'containers/Checkout/PaySection';
import CheckoutShipSection from 'containers/Checkout/ShipSection';
import CheckoutShippingOptionsSection from 'containers/Checkout/melody/ShippingOptionsSection';
import CheckoutReviewSection from 'containers/Checkout/ReviewSection';
import CheckoutChargeSummarySection from 'containers/Checkout/ChargeSummarySection';
import CheckoutOrderTotalSection from 'containers/Checkout/OrderTotalSection';
import ErrorAlert from 'components/checkout/caap/spc/ErrorAlert';
import InfoAlert from 'components/checkout/caap/spc/InfoAlert';
import StepSection from 'components/checkout/caap/StepSection';
import { NotesSection } from 'components/checkout/caap/TermsConditionsAndNotes';
import DecoratedPage from 'components/DecoratedPage';
import { PageLoader, Spinner } from 'components/Loader';
import SiteAwareMetadata from 'components/SiteAwareMetadata';
import {
  getPathFromStep,
  getStepFromPath
} from 'helpers/CheckoutFlowControl';
import { getEventNameForStep, inIframe, isDigitalDeliveryOnlyCart } from 'helpers/CheckoutUtils';
import { mountFixedViewport, unMountFixedViewport } from 'helpers/viewportHack';
import css from 'styles/containers/checkout.scss';

export class Checkout extends Component {

  componentDidMount() {
    const { fetchCheckoutData, location: { search }, wasLastPageOrderConfirmation, resetCartState, resetToDefaultState, triggerAssignment } = this.props;

    // cover our bases in case user client routes off checkout and then client routes back to checkout
    triggerAssignment(HYDRA_CHECKOUT_SHIP_SECTION_ADJUSTMENT);
    triggerAssignment(HYDRA_CHECKOUT_MOVE_TO_FAVORITES);
    resetToDefaultState();
    resetCartState();

    if (wasLastPageOrderConfirmation) {
      return;
    }

    const { pid } = queryString.parse(search);
    trackEvent('TE_PV_NATIVECHECKOUT');
    fetchCheckoutData({
      purchaseId: pid,
      initializeAddressesAndPayments: true,
      includeShipmentOptions: true
    });

    // Add meta viewport to fix tablet
    mountFixedViewport();
  }

  componentDidUpdate(prevProps) {
    const {
      addToCheckoutReferrerHistory,
      checkoutData: {
        addresses: { isLoaded: nextAddressesIsLoaded, saved: savedAddresses },
        paymentTypes: { isLoaded: nextPaymentTypesIsLoaded, saved: savedPayments },
        purchaseData: {
          hasTrackedPageView: nextHasTrackedPageView,
          isLoaded: nextPurchaseDataIsLoaded,
          maxAvailableStep,
          pageHistory,
          savedAddressAndPaymentsFetched,
          targetStep
        },
        shipOptions: { isLoaded: nextShipOptionsIsLoaded }
      },
      goToCart,
      handleInvalidCheckoutPath,
      location: { pathname: nextPath, query: { referrer } },
      removeFromCheckoutReferrerHistory,
      updateCheckoutForTargetStep,
      checkoutData
    } = this.props;

    if (!nextPath.includes('checkout')) {
      return;
    }

    // if we are on the cart step, means we do not have items and need to be kicked back to cart
    if (targetStep === CART_STEP) {
      goToCart();
    }

    const {
      location: { pathname: prevPath },
      checkoutData: {
        purchaseData: { isLoaded: purchaseDataIsLoaded, maxAvailableStep: prevMaxAvailableStep },
        addresses: { isLoaded: addressesIsLoaded },
        paymentTypes: { isLoaded: paymentTypesIsLoaded },
        shipOptions: { isLoaded: shipOptionsIsLoaded }
      },
      trackHasTrackedPageView
    } = prevProps;

    // only send amethyst event once all data is loaded as we want a full checkout picture
    const allDataLoaded = nextPurchaseDataIsLoaded && nextAddressesIsLoaded && nextPaymentTypesIsLoaded && nextShipOptionsIsLoaded;
    const somePrevDataNotLoaded = !purchaseDataIsLoaded || !addressesIsLoaded || !paymentTypesIsLoaded || !shipOptionsIsLoaded;
    if (allDataLoaded && somePrevDataNotLoaded) {
      titaniteView();
      track(pvCheckout, { ...checkoutData });
    }

    if (referrer && !pageHistory.includes(referrer)) {
      addToCheckoutReferrerHistory(referrer);
    }

    const stepForNextPath = getStepFromPath(nextPath);
    const stepForPrevPath = getStepFromPath(prevPath);
    const checkoutPage = CHECKOUT_PAGE_MAP[stepForNextPath] || false;

    // if we hit a base page (address, pay, spc), remove it from the history
    if (checkoutPage && pageHistory.includes(checkoutPage)) {
      removeFromCheckoutReferrerHistory(checkoutPage);
    }

    // haven't received payments and addresses yet, so we can't be sure where user can go yet
    if (!savedAddressAndPaymentsFetched) {
      return;
    }

    // handle tracking page views
    const shouldTrackEvent = stepForNextPath !== TBD_STEP
      && (!nextHasTrackedPageView || prevPath !== nextPath && stepForNextPath <= maxAvailableStep);

    if (shouldTrackEvent) {
      const eventName = getEventNameForStep({ step: stepForNextPath, hasSavedAddresses: savedAddresses.length, hasSavedPayments: savedPayments.length });
      trackEvent(eventName);
      trackHasTrackedPageView();
    }

    // if targetStep is TBD, set it from URL if possible, else just use the max step
    if (targetStep === TBD_STEP) {
      if (stepForNextPath !== TBD_STEP) {
        if (stepForNextPath <= maxAvailableStep) {
          updateCheckoutForTargetStep({ targetStep: stepForNextPath, newUrl: nextPath, asRedirect: true, preserveQueryString: true });
        } else {
          handleInvalidCheckoutPath(getPathFromStep(maxAvailableStep));
        }
      } else {
        const maxAvailableStepPath = getPathFromStep(maxAvailableStep);
        updateCheckoutForTargetStep({ targetStep: maxAvailableStep, newUrl: maxAvailableStepPath, asRedirect: true, preserveQueryString: true });
      }
      return;
    }

    // if targetStep was not TBD, need to figure out what to show

    // if prevPath was /checkout (TBD_STEP), then do nothing, as the app determined where to go
    if (stepForPrevPath === TBD_STEP) {
      return;
    }

    // attempt to go to the nextPath if its valid given that:
    if (prevPath !== nextPath) {
      if (stepForNextPath <= maxAvailableStep) {
        updateCheckoutForTargetStep({ targetStep: stepForNextPath, newUrl: nextPath, preserveQueryString: true });
      } else {
        handleInvalidCheckoutPath(getPathFromStep(maxAvailableStep));
      }
    } else {
      // we don't have to go anywhere except for special cases, 1 example:
      // user has a promo/gc that covers cost of the purchase and is on SPC,
      // user then updates the quantity of an item so that a CC is required
      // since that action takes place on SPC, there is no path change, so we need to update
      // things if for some reason our maxAvailableStep lowered from SPC
      if (stepForNextPath > maxAvailableStep && maxAvailableStep !== prevMaxAvailableStep) {
        const maxPath = getPathFromStep(maxAvailableStep);
        updateCheckoutForTargetStep({ targetStep: maxAvailableStep, newUrl: maxPath, preserveQueryString: true });
      }
    }
  }

  componentWillUnmount() {
    const { resetToDefaultState } = this.props;
    resetToDefaultState();

    // Remove meta viewport to fix tablet
    unMountFixedViewport();
  }

  render() {
    const {
      checkoutData: {
        addresses,
        shipOptions,
        paymentTypes,
        purchaseData: {
          cartType,
          constraintOverlayMessage,
          data: { chargeSummary },
          isLoaded,
          savedAddressAndPaymentsFetched,
          targetStep,
          isFetchingCheckoutData,
          isNewCustomer
        }
      },
      isAssignedInSSA,
      wasLastPageOrderConfirmation
    } = this.props;

    const hideHeaderFooter = inIframe() || 'MOBILE';

    const {
      saved: savedPayments
    } = paymentTypes;

    const {
      saved: savedAddresses
    } = addresses;

    const { testId } = this.context;

    if (wasLastPageOrderConfirmation) {
      return (
        <DecoratedPage hideHeader={hideHeaderFooter} hideFooter={hideHeaderFooter} bodyPageClass="checkoutPage">
          <div className={css.orderPlaced}>Your order has been placed.  If you wish to modify or cancel it, please do so from your <Link to="/orders">order history</Link>.</div>
        </DecoratedPage>
      );
    }

    if (!isLoaded || !savedAddressAndPaymentsFetched) {
      return (
        <DecoratedPage hideHeader={hideHeaderFooter} hideFooter={hideHeaderFooter} bodyPageClass="checkoutPage">
          <PageLoader />
        </DecoratedPage>
      );
    }

    const { shippingCharge } = chargeSummary;
    const mobileSpinnerStyles = cn(css.checkoutSpinner, {
      [css.active]: !!isFetchingCheckoutData
    });

    const isNewPaymentStep = targetStep === NEW_PAYMENT_STEP;
    const isNewAddressStep = targetStep === NEW_ADDRESS_STEP || targetStep === NEW_BILLING_ADDRESS_STEP;
    const isEditAddressStep = targetStep === EDIT_ADDRESS_STEP || targetStep === EDIT_BILLING_ADDRESS_STEP;
    const isSavedAddressStep = targetStep === SAVED_ADDRESS_STEP || targetStep === SELECT_BILLING_ADDRESS_STEP;

    let showAsModal = false;

    if (isSavedAddressStep || isNewAddressStep || isEditAddressStep) {
      showAsModal = !isNewCustomer && savedAddresses && savedAddresses.length;
    } else if (isNewPaymentStep) {
      showAsModal = savedPayments && savedPayments.length;
    }

    let bodyClassPage = 'checkoutPage';
    if (isFetchingCheckoutData && showAsModal) {
      bodyClassPage = 'checkoutPageLoadingAsModal';
    } else if (isFetchingCheckoutData) {
      bodyClassPage = 'checkoutPageLoading';
    } else if (showAsModal) {
      bodyClassPage = 'checkoutPageAsModal';
    }

    const excludeShipOptions = isAssignedInSSA;
    const isDigitalDeliveryOnly = isDigitalDeliveryOnlyCart(cartType);
    const reviewStepNumber = excludeShipOptions && !isDigitalDeliveryOnly ? 4 : 3;

    return (
      <SiteAwareMetadata>
        <DecoratedPage hideHeader={hideHeaderFooter} hideFooter={hideHeaderFooter} bodyPageClass={bodyClassPage}>
          <div className={mobileSpinnerStyles} data-test-id={testId('spinner')}>
            <Spinner viewBox="50" strokeWidth="3" size="125" />
          </div>

          <div className={css.checkoutBody}>

            <div className={css.showOnlySmallScreen} data-test-id={testId('mobileAlert')}>
              <ErrorAlert constraintOverlayMessage={constraintOverlayMessage} />
            </div>

            <div className={css.leftCol}>
              <div ref={el => this.spcRef = el}>
                <div className={css.hideSmallScreen} data-test-id={testId('desktopAlert')}>
                  <ErrorAlert constraintOverlayMessage={constraintOverlayMessage} />
                </div>

                <InfoAlert shippingCharge={shippingCharge} />
                <CheckoutShipSection />
                <CheckoutPaySection />
                {excludeShipOptions && <CheckoutShippingOptionsSection /> }
                <CheckoutReviewSection excludeShipOptions={excludeShipOptions} reviewStepNumber={reviewStepNumber} />
                <CheckoutGiftOptionsSection />
                <CheckoutOrderTotalSection />
              </div>
              <StepSection noBorder={true} isCurrentStep={targetStep === REVIEW_STEP && !shipOptions.showModal} dataTestIdLoader={`${testId('loader')}`}>
                <NotesSection />
              </StepSection>
            </div>

            <div className={css.rightCol}>
              <CheckoutChargeSummarySection spcRef={this.spcRef} />
            </div>

          </div>
        </DecoratedPage>
      </SiteAwareMetadata>
    );
  }
}

function mapStateToProps(state) {
  const { checkoutData, history: { wasLastPageOrderConfirmation }, routing: { locationBeforeTransitions } } = state;
  const isAssignedInSSA = isAssigned(HYDRA_CHECKOUT_SHIP_SECTION_ADJUSTMENT, 1, state);

  return {
    checkoutData,
    isAssignedInSSA,
    location: locationBeforeTransitions,
    wasLastPageOrderConfirmation
  };
}

Checkout.contextTypes = {
  testId: PropTypes.func
};

export default connect(mapStateToProps, {
  addToCheckoutReferrerHistory,
  fetchCheckoutData,
  goToCart,
  handleInvalidCheckoutPath,
  removeFromCheckoutReferrerHistory,
  resetCartState,
  resetToDefaultState,
  trackHasTrackedPageView,
  triggerAssignment,
  updateCheckoutForTargetStep
})(Checkout);
